import { usePricePredictionEligibility } from '@mfs/client/common/hooks/usePricePredictionEligibility/usePricePredictionEligibility'
import { useFeature } from '@pcln/feature-components'
import { PriceWatchToggleCard } from '@pcln/fly-ui-components'
import { PricePrediction } from '@pcln/horizon-fly-components'
import { Box, useToast } from 'pcln-design-system'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { LONG_TRIP_TYPE_FORMAT } from '../../../common/constants/itinerary'
import {
  logPriceWatchGetAppClick,
  logPriceWatchHandoffDrawerOpen,
  logPriceWatchSubscribe,
  logPriceWatchSubscribeDupe,
  logPriceWatchUnsubscribe,
  logPriceWatchUnsubscribeDupe,
} from '../../../common/log-ga-event'
import { validateEmailFormat } from './helpers'
import {
  requestPriceWatchGetList,
  requestPriceWatchOptIn,
  requestPriceWatchOptOut,
} from './priceWatchRequests'
import { pricePredictionRequest } from './priceWatchRequests/pricePredictionRequest'
import {
  logPricePrediction,
  logPricePredictionError,
} from '@mfs/client/common/log-ga-event/logGaEvent-price-prediction'

const PricePredictionContainer = ({
  plfCode,
  appCode,
  dealPrice,
  dealType,
  departDate,
  destCityId,
  originCityId,
  returnDate,
  tripType,
  destCityCode,
  destCityName,
  originCityCode,
  originCityName,
  destAirportCode,
  originAirportCode,
  isMobile,
  cguid,
  minRetailPrice,
}) => {
  const isPriceWatchEnabled = useFeature('flights.enablePriceWatch', true)
  const [isToggleOn, setIsToggleOn] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const authUser = useSelector((state) => state.pclnBootstrapData?.authUser)
  const hasAppInstalled = useSelector((state) => state.hasAppInstalled)
  const isUserLoggedIn = Boolean(authUser)
  const signedInEmail = authUser?.email ?? ''
  const watchIdsRef = useRef(null)
  const { addToast } = useToast()
  const isDisabledRef = useRef(false)
  const useLocalFlyql = useSelector((state) => state.pclnBootstrapData?.useLocalFlyql)
  const appVersion = useSelector((state) => state.pclnBootstrapData?.appVersion)
  const [recommendationInfo, setRecommendationInfo] = useState({})

  const { isPricePredictionEligible, shouldShowPricePrediction } = usePricePredictionEligibility(
    !!recommendationInfo?.text
  )

  useEffect(() => {
    if (!validateEmailFormat(signedInEmail)) {
      return
    }

    function getIsCurrentItinerarySubscribed(priceWatches) {
      const isCurrentItinerarySubscribed = priceWatches?.find?.((priceWatch) => {
        return (
          priceWatch?.destCityId === destCityId &&
          priceWatch?.originCityId === originCityId &&
          priceWatch?.tripType === LONG_TRIP_TYPE_FORMAT[tripType]
        )
      })
      return Boolean(isCurrentItinerarySubscribed)
    }

    async function requestGetListAndSetToggle() {
      const priceWatchGetListResponse = await requestPriceWatchGetList({
        departDate,
        destCityId,
        email: signedInEmail,
        originCityId,
        returnDate,
        destCityCode,
        originCityCode,
        useLocalFlyql,
      })
      const priceWatches = priceWatchGetListResponse?.priceWatches
      if (!priceWatches) {
        handleGetPriceWatchListError()
      }
      watchIdsRef.current = priceWatches?.map?.((priceWatch) => priceWatch?.watchId)
      const isCurrentItinerarySubscribed =
        priceWatches?.length > 0 && getIsCurrentItinerarySubscribed(priceWatches)
      isCurrentItinerarySubscribed ? setIsToggleOn(true) : setIsToggleOn(false)
      setIsLoading(false)
    }

    if (
      departDate &&
      destCityId &&
      signedInEmail &&
      originCityId &&
      destCityCode &&
      originCityCode &&
      isPriceWatchEnabled
    ) {
      requestGetListAndSetToggle()
    }
  }, [
    signedInEmail,
    departDate,
    destCityId,
    originCityCode,
    returnDate,
    isPriceWatchEnabled,
    originCityId,
    destCityCode,
    tripType,
    useLocalFlyql,
  ])

  useEffect(() => {
    async function requestPricePredictionRecommendation() {
      try {
        const recommendations = await pricePredictionRequest({
          departDate,
          returnDate,
          origin: originAirportCode,
          destination: destCityCode,
          minimumRetailPrice: minRetailPrice,
          isMobile,
          email: signedInEmail,
          appVersion,
        })

        if (recommendations.length > 0) {
          const { recommendation, recommendationText } = recommendations[0]
          setRecommendationInfo({ isBuyNow: !!recommendation, text: recommendationText })
        }

        logPricePrediction({ recommendations })
      } catch (error) {
        logPricePredictionError()
      } finally {
        setIsLoading(false)
      }
    }

    if (isPricePredictionEligible) {
      requestPricePredictionRecommendation()
    }
  }, [
    appVersion,
    departDate,
    destCityCode,
    isMobile,
    isPricePredictionEligible,
    minRetailPrice,
    originAirportCode,
    returnDate,
    signedInEmail,
  ])

  const showRequestFailureToast = () => {
    addToast({
      children: `Something went wrong with price watch, please try again later`,
      color: 'success',
      colorScheme: 'warningLight',
    })
  }

  const handleSubscribeSuccess = (priceWatchOptInResponse) => {
    setIsLoading(false)
    setIsToggleOn(true)
    isDisabledRef.current = false
    watchIdsRef.current = [priceWatchOptInResponse?.watchId]
  }

  const handleSubscribeError = () => {
    showRequestFailureToast()
    setIsLoading(false)
    isDisabledRef.current = false
  }
  const handleUnsubscribeSuccess = () => {
    isDisabledRef.current = false
    setIsToggleOn(false)
  }

  const handleUnsubscribeError = () => {
    showRequestFailureToast()
    isDisabledRef.current = false
  }

  const handleGetPriceWatchListError = () => {
    setIsLoading(false)
  }

  const isValidResponse = ({ status, statusCode }) => status === 'SUCCESS' || statusCode === '409 CONFLICT'

  const subscribeToPriceWatch = async () => {
    if (isDisabledRef.current) {
      logPriceWatchSubscribeDupe({ tripType, isUserSignedIn: isUserLoggedIn })
      return
    }
    isDisabledRef.current = true
    logPriceWatchSubscribe({ tripType, isUserSignedIn: isUserLoggedIn })
    const priceWatchOptInResponse = await requestPriceWatchOptIn({
      plfCode,
      appCode,
      dealPrice,
      dealType,
      departDate,
      destCityId,
      email: signedInEmail,
      originCityId,
      returnDate,
      tripType,
      destCityCode,
      destCityName,
      originCityCode,
      originCityName,
      useLocalFlyql,
    })
    isValidResponse(priceWatchOptInResponse)
      ? handleSubscribeSuccess(priceWatchOptInResponse)
      : handleSubscribeError()
  }

  const pageId = isMobile ? 'MWEB_FLT_LISTINGS_PRICEWATCH' : 'DESKTOP_FLT_LISTINGS_PRICEWATCH'
  const smartLink = `https://priceline.smart.link/yihao580h?departDate=${departDate}&departDestinationAirport=${destAirportCode}&departOriginAirport=${originAirportCode}&device_id=null&returnDate=${returnDate}&returnDestinationAirport=${originAirportCode}&site_id=1&PAGEID=${pageId}&sub_site_id=${cguid}`
  const unsubscribeToPriceWatch = async () => {
    if (isDisabledRef.current) {
      logPriceWatchUnsubscribeDupe({ tripType, isUserSignedIn: isUserLoggedIn })
      return
    }
    isDisabledRef.current = true
    const watchIds = watchIdsRef.current
    if (!watchIds?.length > 0) {
      return
    }
    logPriceWatchUnsubscribe({ tripType, isUserSignedIn: isUserLoggedIn })
    const priceWatchOptOutResponse = await requestPriceWatchOptOut({ watchIds, unsubscribeAll: false })
    isValidResponse(priceWatchOptOutResponse) ? handleUnsubscribeSuccess() : handleUnsubscribeError()
  }

  const onToggle = async () => {
    if (!isToggleOn) {
      subscribeToPriceWatch()
    } else {
      unsubscribeToPriceWatch()
    }
    logPriceWatchHandoffDrawerOpen({ tripType, isUserSignedIn: isUserLoggedIn })
  }

  if (!isLoading && (shouldShowPricePrediction || (isUserLoggedIn && isPriceWatchEnabled))) {
    return (
      <Box mb={[2, null, null, null, 3]} px={[2, null, null, null, 0]}>
        {shouldShowPricePrediction ? (
          <PricePrediction
            isUserSignedIn={isUserLoggedIn}
            isPriceWatchEnabled={isPriceWatchEnabled}
            recommendationInfo={recommendationInfo}
            isToggleOn={isToggleOn}
            onToggle={onToggle}
            smartLink={smartLink}
            onGetAppClick={() => logPriceWatchGetAppClick({ tripType, isUserSignedIn: isUserLoggedIn })}
            hasAppInstalled={hasAppInstalled}
          />
        ) : (
          <PriceWatchToggleCard
            isToggleOn={isToggleOn}
            onToggle={onToggle}
            isMobile={isMobile}
            smartLink={smartLink}
            onGetAppClick={() => logPriceWatchGetAppClick({ tripType, isUserSignedIn: isUserLoggedIn })}
            onAccordionOpen={(isOpen) =>
              logPriceWatchHandoffDrawerOpen({ tripType, isUserSignedIn: isUserLoggedIn, isOpen })
            }
            hasAppInstalled={hasAppInstalled}
          />
        )}
      </Box>
    )
  }
}

export { PricePredictionContainer }
